import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { FullPageState, Spinner } from '../components/ui.js';

/**
 * Public page for an admin-minted reset link.
 *
 * The route sits outside the auth gate deliberately (see App.tsx): the page
 * renders for signed-in visitors too, because an admin may open their own link
 * while signed in. The pre-validation query decides which state renders, so an
 * expired or spent link shows a friendly message rather than failing on submit.
 */
export function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const check = useQuery({
    queryKey: ['reset-password', token],
    queryFn: () => api.auth.checkResetToken(token!),
    retry: false,
  });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: () => api.auth.resetPassword(token!, newPassword),
    onSuccess: () => {
      toast.info('Password updated. Sign in with your new password.');
      navigate('/login');
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.'),
  });

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    reset.mutate();
  }

  if (check.isLoading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Checking the link…" />
      </div>
    );
  }

  if (check.error) {
    return (
      <FullPageState title="This link is no longer valid">
        <p>
          It may have expired, been used already, or been replaced by a newer one. Ask an
          administrator for a fresh link.
        </p>
        <Link to="/login" className="btn-ghost mt-4 inline-flex">
          Back to sign in
        </Link>
      </FullPageState>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="text-3xl" aria-hidden>
            🔑
          </div>
        </div>

        <h1 className="mb-4 text-center text-xl font-semibold">
          Set a new password for {check.data?.email}
        </h1>

        {/* A real form, so required and minLength run in the browser before the
            request — the ProfilePage lesson. */}
        <form onSubmit={onSubmit} className="surface space-y-3 p-5">
          <label className="block">
            <span className="mb-1 block text-sm font-medium">New password</span>
            <input
              className="field"
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              minLength={10}
              autoComplete="new-password"
              aria-describedby="password-hint"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium">Confirm new password</span>
            <input
              className="field"
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              minLength={10}
              autoComplete="new-password"
              aria-describedby="password-hint"
            />
          </label>

          <p id="password-hint" className="text-xs text-ink/50">
            At least 10 characters. Length beats complexity — a short phrase works well.
          </p>

          {error ? (
            <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </p>
          ) : null}

          <button type="submit" className="btn-primary w-full" disabled={reset.isPending}>
            {reset.isPending ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
}